import type { CoincidentAggregation, XyzDataPoint } from '../types';
import { maxOf, minOf } from './ranges';

/** A position in the xy-plane and the single z the components there reduce to. */
export type CollapsedSample = {
  x: number;
  y: number;
  z: number;
  /** How many components landed on this position. */
  count: number;
  /** Every component that contributed, so a hit-test can name them all. */
  points: XyzDataPoint[];
};

/** Several values reduced to one, the way coincident components are. */
export const aggregate = (
  values: number[],
  aggregation: CoincidentAggregation
): number => {
  if (aggregation === 'sum')
    return values.reduce((sum, value) => sum + value, 0);
  if (aggregation === 'max') return maxOf(values);
  if (aggregation === 'min') return minOf(values);

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

/**
 * Reduces components sharing an (x, y) position to one sample per position.
 *
 * Required before triangulation rather than offered as an option: exact duplicates
 * produce zero-area triangles, and Ardoq's numeric fields are commonly 1-5 scores, so a
 * whole estate routinely lands on a couple of dozen distinct positions. Skipping this
 * step does not make a surface less accurate, it makes the triangulation degenerate.
 *
 * Positions are keyed on exact field values. Two components a floating-point hair apart
 * stay separate, which delaunator tolerates; it is the exactly-equal case that has to be
 * merged, and field values that came from the same source compare exactly.
 */
export const collapseCoincident = (
  points: XyzDataPoint[],
  aggregation: CoincidentAggregation
): CollapsedSample[] => {
  const byPosition = new Map<string, XyzDataPoint[]>();

  for (const point of points) {
    const key = `${point.x}|${point.y}`;
    const existing = byPosition.get(key);
    if (existing) {
      existing.push(point);
      continue;
    }
    byPosition.set(key, [point]);
  }

  return [...byPosition.values()].map(atPosition => ({
    x: atPosition[0].x,
    y: atPosition[0].y,
    z: aggregate(
      atPosition.map(({ z }) => z),
      aggregation
    ),
    count: atPosition.length,
    points: atPosition,
  }));
};
